import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveDropdownDataSource,
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../common/storage-manager.class';
import { FincasModel } from '../../../../models/form-common/fincas.module';
import { ProductosModel } from '../../../../models/productos/productos.model';
import { StockageModel } from '../../../../models/stock/stokage.model';
import { FincasApiService } from '../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../service/api/productos-api';
import { StockApiService } from '../../../../service/api/stock-api.service';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import { DashboardService } from '../../../../service/events/dashboard.service';
import { BaseForm } from '../../../base-form';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-salidas-form',
    standalone: false,
    templateUrl: './salidas-form.component.html',
    styleUrls: ['./salidas-form.component.scss'],
})
export class SalidasFormComponent extends BaseForm<StockageModel> implements OnInit {
    public readonly fincaName = environment.features.fincaName;
    public readonly fincasName = environment.features.fincasName;

    public override model: StockageModel = { stockage_real: 0, stockage_teorico: 0, fecha: '', unidades_temp: 0, unidades: 0 };
    public formFields!: Form<StockageModel>;

    public readonly reactiveFormLoadingLabel = 'Preparando formulario de salida de stock...';

    public isReactiveFormReady = false;

    public reactiveFormLoadingError = '';

    public initialPreviousData: StockageModel | null = null;

    public reactiveFormRecordId = '';

    public getRequest!: RequestHandler<StockageModel[]>;

    constructor(
        public fincasApi: FincasApiService,
        public productosApi: ProductosApiService,
        public override router: Router,
        public override route: ActivatedRoute,
        public dashboardEvents: DashboardService,
        public stockApi: StockApiService,
    ) {
        super(
            route,
            router,
            dashboardEvents,
            stockApi.salidas.PUT,
            stockApi.salidas.POST,
            'salidas',
            'Editar salida',
            'Crear salida',
        );

        this.getRequest = this.stockApi.salidas.GET;
    }

    public ngOnInit(): void {
        void this.initializeSalidasForm();
    }

    private async initializeSalidasForm(): Promise<void> {
        try {
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toStockageModel(this.getModel() as unknown as Record<string, unknown>);
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            const initialModel = this.initializeHostModel({
                ...this.model,
                ...routeModel,
            });

            this.initialPreviousData = this.getInitialPreviousData(initialModel);
            await this.prepareInitialDropdownState();
            this.formFields = this.createFormFields();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.finalizeExternalDropdowns();
            console.error('No se ha podido preparar el formulario de salidas de stock.', error);
        }
    }

    public override goBack(): void {
        this.router.navigate(['dashboard', 'stock-salidas']);
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<StockageModel>): void {
        const stableValue = this.captureStableValueChange(event.value as StockageModel);

        this.model = this.toStockageModel(stableValue);
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleFormValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<StockageModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged' && event.type !== 'dropdownRefresh') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as StockageModel);

        this.model = this.toStockageModel(stableValue);
        this.syncModelFromForm();
    }

    private handleFormValueChange(
        formModel: ReactiveFormValue<StockageModel>,
        changedField: ReactiveFormChangedField<StockageModel>,
    ): void {
        const changedFieldName = String(changedField ?? '');

        if (changedFieldName === '') {
            this.model = this.toStockageModel(formModel);
            this.syncModelFromForm();

            return;
        }

        this.model = this.toStockageModel(formModel);
        this.syncModelFromForm();
    }


    private prepareInitialDropdownState(): Promise<void> {
        return Promise.resolve();
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.finalizeExternalDropdowns();
        this.refreshExternalDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toStockageModel(this.model as unknown as Record<string, unknown>);

        this.updateHostModel(normalizedModel);
    }

    private refreshExternalDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private getInitialPreviousData(routeModel: StockageModel): StockageModel | null {
        return Object.keys(routeModel as Record<string, unknown>).length === 0
            ? null
            : this.toStockageModel(routeModel as unknown as Record<string, unknown>);
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return `No se ha podido cargar el formulario de salida de stock: ${error.message}`;
        }

        return 'No se ha podido cargar el formulario de salida de stock. Revisa la conexión y vuelve a intentarlo.';
    }

    private toStockageModel(value: Record<string, unknown> | ReactiveFormValue<StockageModel> | StockageModel): StockageModel {
        return { ...(value as Record<string, unknown>) } as unknown as StockageModel;
    }

    private createFormFields(): Form<StockageModel> {
        return {
            name: 'salidas',
            header: 'Registrar Salida',
            type: +this.getType() as FormType,
            sections: this.getSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            dropdownCatalogs: {
                critical: ['id_finca'],
                deferred: ['id_producto1', 'id_producto2', 'id_producto3'],
                lazy: []
            },
            previousDataTransform: (previousData: StockageModel): StockageModel => this.toStockageModel(previousData),
        };
    }

    private getSections(): FormSection<StockageModel>[] {
        return [
            {
                name: '',
                fields: this.getVisibleFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
        ];
    }

    private getVisibleFields(): FormField<StockageModel>[] {
        return [
            this.dropdownField(
                'id_finca',
                this.fincaName,
                this.createDropdownData<FincasModel>(),
                1,
                this.fincasDataSource(),
            ),
            this.emptyField(2),
            this.emptyField(3),
            this.dropdownField(
                'id_producto1',
                'Producto',
                this.createDropdownData<ProductosModel>(),
                4,
                this.productosDataSource(),
            ),
            this.textField('unidades1', 'Unidades', 5),
            this.dropdownField(
                'id_producto2',
                '',
                this.createDropdownData<ProductosModel>(),
                6,
                this.productosDataSource(),
            ),
            this.textField('unidades2', '', 7),
            this.dropdownField(
                'id_producto3',
                '',
                this.createDropdownData<ProductosModel>(),
                8,
                this.productosDataSource(),
            ),
            this.textField('unidades3', '', 9),
            this.textField('albaran', 'Albarán', 10),
            {
                name: 'fecha',
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 6,
                conditions: this.conditions(),
                order: 11,
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: false,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 12,
            },
        ];
    }

    private getHiddenFields(): FormField<StockageModel>[] {
        return [
            this.hiddenField('id', undefined, 1),
            this.hiddenField('id_usuario', StorageManager.getUser()?.id ?? '', 2),
            this.hiddenField('tiposalida', 'S', 3),
        ];
    }

    private dropdownField(
        name: string,
        label: string,
        data: DropdownData<unknown>,
        order: number,
        dataSource: ReactiveDropdownDataSource<StockageModel>,
    ): FormField<StockageModel> {
        return {
            name,
            label,
            input: {
                type: InputType.DROPDOWN,
                isEditable: true,
                data,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id',
                },
            },
            dataSource,
            width: 4,
            conditions: this.conditions(),
            order,
        };
    }

    private fincasDataSource(): ReactiveDropdownDataSource<StockageModel> {
        return {
            endpoint: this.fincasApi.fincasGET,
            cacheKey: 'stock-salidas-fincas',
            labelKey: 'nombre',
            valueKey: 'id',
            distinctBy: 'id',
            cacheEmptyResults: false,
        };
    }

    private productosDataSource(): ReactiveDropdownDataSource<StockageModel> {
        return {
            endpoint: this.productosApi.productos.GET,
            cacheKey: 'stock-salidas-productos',
            labelKey: 'nombre',
            valueKey: 'id',
            distinctBy: 'id',
            cacheEmptyResults: false,
        };
    }

    private createDropdownData<TValue>(): DropdownData<TValue> {
        return {
            filtered: [],
            values: [],
        };
    }

    private textField(name: string, label: string, order: number): FormField<StockageModel> {
        return {
            name,
            label,
            input: {
                type: InputType.TEXT,
                isEditable: true,
            },
            width: 4,
            conditions: this.conditions(),
            order,
        };
    }

    private emptyField(order: number): FormField<StockageModel> {
        return {
            name: `empty_${order}`,
            label: '',
            input: {
                type: InputType.EMPTY,
                isEditable: false,
            },
            width: 4,
            conditions: this.conditions({ isRetained: true }),
            order,
        };
    }

    private hiddenField(name: string, defaultValue: unknown, order: number): FormField<StockageModel> {
        return {
            name,
            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: false,
                defaultValue: defaultValue as string | number | boolean | null | undefined,
            },
            width: 0,
            conditions: this.conditions(),
            order,
        };
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
